import './PuxTooltip.scss'

import React, {
  FunctionComponent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

export type PuxTooltipPlacement = 'top' | 'bottom' | 'left' | 'right'

export interface PuxTooltipProps {
  content: ReactNode
  placement?: PuxTooltipPlacement
  className?: string
  bubbleClassName?: string
  children?: ReactNode
}

const FLIP_MAP: Record<PuxTooltipPlacement, PuxTooltipPlacement> = {
  top: `bottom`,
  bottom: `top`,
  left: `right`,
  right: `left`,
}

const VIEWPORT_MARGIN = 8
const TOOLTIP_OPEN_EVENT = `pux-tooltip-open`
const useIsomorphicLayoutEffect =
  typeof window !== `undefined` ? useLayoutEffect : useEffect

let nextTooltipId = 0

const PuxTooltip: FunctionComponent<PuxTooltipProps> = ({
  content,
  placement = `top`,
  className,
  bubbleClassName,
  children,
}) => {
  const tooltipIdRef = useRef(0)
  const rootRef = useRef<HTMLSpanElement | null>(null)
  const bubbleRef = useRef<HTMLSpanElement | null>(null)
  const [hoverOpen, setHoverOpen] = useState(false)
  const [focusOpen, setFocusOpen] = useState(false)
  const [clickOpen, setClickOpen] = useState(false)
  const [hoverSuppressed, setHoverSuppressed] = useState(false)
  const [
    resolvedPlacement,
    setResolvedPlacement,
  ] = useState<PuxTooltipPlacement>(placement)
  const [shift, setShift] = useState(0)
  const open = clickOpen || focusOpen || (hoverOpen && !hoverSuppressed)

  if (tooltipIdRef.current === 0) {
    tooltipIdRef.current = ++nextTooltipId
  }

  const measure = useCallback(() => {
    const root = rootRef.current
    const bubble = bubbleRef.current
    if (!root || !bubble || typeof window === `undefined`) return

    const rootRect = root.getBoundingClientRect()
    const bubbleRect = bubble.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight

    let next = placement
    if (
      placement === `top` &&
      rootRect.top - bubbleRect.height - VIEWPORT_MARGIN < 0
    ) {
      next = FLIP_MAP.top
    } else if (
      placement === `bottom` &&
      rootRect.bottom + bubbleRect.height + VIEWPORT_MARGIN > vh
    ) {
      next = FLIP_MAP.bottom
    } else if (
      placement === `left` &&
      rootRect.left - bubbleRect.width - VIEWPORT_MARGIN < 0
    ) {
      next = FLIP_MAP.left
    } else if (
      placement === `right` &&
      rootRect.right + bubbleRect.width + VIEWPORT_MARGIN > vw
    ) {
      next = FLIP_MAP.right
    }
    setResolvedPlacement(next)

    let nextShift = 0
    if (next === `top` || next === `bottom`) {
      const bubbleLeft =
        rootRect.left + rootRect.width / 2 - bubbleRect.width / 2
      const bubbleRight = bubbleLeft + bubbleRect.width
      if (bubbleLeft < VIEWPORT_MARGIN) {
        nextShift = VIEWPORT_MARGIN - bubbleLeft
      } else if (bubbleRight > vw - VIEWPORT_MARGIN) {
        nextShift = vw - VIEWPORT_MARGIN - bubbleRight
      }
    }
    setShift(nextShift)
  }, [placement])

  useIsomorphicLayoutEffect(() => {
    if (!open) return
    measure()
    const handle = () => measure()
    window.addEventListener(`scroll`, handle, true)
    window.addEventListener(`resize`, handle)
    return () => {
      window.removeEventListener(`scroll`, handle, true)
      window.removeEventListener(`resize`, handle)
    }
  }, [open, measure])

  useEffect(() => {
    if (!open || typeof document === `undefined`) return

    const handlePointerDown = (event: PointerEvent) => {
      const root = rootRef.current
      if (!root || root.contains(event.target as Node)) return

      setClickOpen(false)
      setFocusOpen(false)
      setHoverOpen(false)
      setHoverSuppressed(false)
    }

    document.addEventListener(`pointerdown`, handlePointerDown)
    return () => {
      document.removeEventListener(`pointerdown`, handlePointerDown)
    }
  }, [open])

  const closeTooltip = () => {
    setClickOpen(false)
    setFocusOpen(false)
    setHoverOpen(false)
    setHoverSuppressed(true)
  }

  useEffect(() => {
    if (typeof window === `undefined`) return

    const handleTooltipOpen = (event: Event) => {
      const openedTooltipId = (event as CustomEvent<{ id: number }>).detail?.id
      if (openedTooltipId !== tooltipIdRef.current) {
        closeTooltip()
      }
    }

    window.addEventListener(TOOLTIP_OPEN_EVENT, handleTooltipOpen)
    return () => {
      window.removeEventListener(TOOLTIP_OPEN_EVENT, handleTooltipOpen)
    }
  }, [])

  const handlePointerDown = (event: React.PointerEvent<HTMLSpanElement>) => {
    event.stopPropagation()

    if (clickOpen) {
      closeTooltip()
      return
    }

    window.dispatchEvent(
      new CustomEvent(TOOLTIP_OPEN_EVENT, {
        detail: { id: tooltipIdRef.current },
      })
    )
    setClickOpen(true)
    setHoverSuppressed(false)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === `Escape` && open) {
      event.stopPropagation()
      closeTooltip()
    } else if (event.key === `Enter` || event.key === ` `) {
      event.preventDefault()
      if (clickOpen) {
        closeTooltip()
      } else {
        setClickOpen(true)
        setHoverSuppressed(false)
      }
    }
  }

  const rootClass = [`PuxTooltip`, className].filter(Boolean).join(` `)
  const bubbleClass = [`PuxTooltip-bubble`, bubbleClassName]
    .filter(Boolean)
    .join(` `)

  return (
    <span
      className={rootClass}
      onBlur={setFocusOpen.bind(null, false)}
      onFocus={() => {
        setFocusOpen(true)
        setHoverSuppressed(false)
      }}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => {
        setHoverOpen(true)
        setHoverSuppressed(false)
      }}
      onMouseLeave={() => {
        setHoverOpen(false)
        setHoverSuppressed(false)
      }}
      onPointerDown={handlePointerDown}
      ref={rootRef}
    >
      <span className='PuxTooltip-trigger'>{children}</span>
      {open && content ? (
        <span
          className={bubbleClass}
          data-placement={resolvedPlacement}
          ref={bubbleRef}
          role='tooltip'
          style={{ '--PuxTooltip-shift': `${shift}px` } as React.CSSProperties}
        >
          {content}
        </span>
      ) : null}
    </span>
  )
}

export default PuxTooltip
